import { Action } from "@reduxjs/toolkit"
import * as valid from "lib/valid"
import * as opt from "lib/opt"

export function HandleInputChange(fn: (v: string) => void) {
	return (event: React.ChangeEvent<HTMLInputElement>) => {
		fn(event.target.value)
	}
}

export function GetMsgFromError(e: Error | string): string {
	return e instanceof Error ? e.message : typeof e === "string" ? e : "Unknown error type"
}

export function GetRenderKey(v: any, i: int): string {
	return `${v}-${i}`
}

export function ReplaceEl<T>(arr: T[], findPredicate: (el: T) => bool, el: (el: T) => T) {
	const foundI = arr.Position(findPredicate).UnwrapOr(-1)
	arr.splice(foundI, 1, el(arr[foundI]))
}

export function QueueMicroTask(fn: () => void) {
	setTimeout(fn, 0)
}

export function DebugAction(logFn: () => void): Action {
	logFn()
	return {
		type: "debug",
	}
}

export function ProperCase(s: string): string {
	return s
		.Split(" ")
		.Map((w) => w.charAt(0).ToUpper() + w.slice(1).ToLower())
		.Join(" ")
}

export function IsPlainObject(value: any): value is Record<string, any> {
	return (
		typeof value === "object" &&
		value !== null &&
		!Array.isArray(value) &&
		Object.getPrototypeOf(value) === Object.prototype
	)
}

export function Range(start: int, end: int): int[] {
	return Array.from({ length: end - start }, (_, i) => i + start)
}

export function GetTokenExp(t: string):
	| {
			expTime: int
			timeUntilExp: int
			/* eslint-disable-next-line*/
	  }
	| never {
	const [, payloadBase64] = t.Split(".")
	try {
		const payload = JSON.parse(atob(payloadBase64))
		const expTime = Number(payload.exp).Mul(1000)
		const timeUntilExp = expTime - Date.now() - 5 * 60 * 1000
		return {
			expTime,
			timeUntilExp,
		}
	} catch (e) {
		log.Fatal("Error parsing token payload")
	}
}

export function GetInitials(s: string): string {
	// Split on spaces and underscores, take first 2 parts
	return s.split(/[\s_]+/)
		.slice(0, 2)
		.Map(part => part.charAt(0).ToUpper())
		.Join("")
}

export function IsEmpty<T>(v: string | T[]): bool {
	return v.length === 0
}

/**
 * Creates an object with all methods and getters from a class instance bound to that instance
 * @param instance - The class instance to bind methods from
 * @param methodNames - Optional array of specific method/getter names to bind (binds all if not provided)
 * @returns An object with all bound methods and getters
 */
export function BindMethods<T extends object>(
	instance: T,
	methodNames?: (keyof T)[]
): { [K in keyof T]: T[K] } {
	const result: Partial<{ [K in keyof T]: T[K] }> = {}
	const prototype = Object.getPrototypeOf(instance)

	// Get all property names, including non-enumerable ones
	const allProps = [
		...Object.getOwnPropertyNames(prototype),
		...Object.getOwnPropertyNames(instance),
	].Filter((prop) => prop !== "constructor" && prop !== "__proto__")

	// Filter to specific methods if provided
	const propsToProcess = methodNames
		? allProps.Filter((prop) => methodNames.Contains(prop as keyof T))
		: allProps

	for (const prop of propsToProcess) {
		const descriptor =
			Object.getOwnPropertyDescriptor(prototype, prop) ||
			Object.getOwnPropertyDescriptor(instance, prop)

		if (!descriptor) continue

		// Handle getters
		if (descriptor.get) {
			Object.defineProperty(result, prop, {
				get: descriptor.get.bind(instance),
				enumerable: true,
			})
		}
		// Handle methods
		else if (typeof instance[prop as keyof T] === "function") {
			result[prop as keyof T] = (instance[prop as keyof T] as Function).bind(instance) as any
		}
		// Handle instance properties
		else {
			result[prop as keyof T] = instance[prop as keyof T]
		}
	}

	return result as { [K in keyof T]: T[K] }
}

/**
 * Compares two arrays for equality using a custom comparison function for elements
 *
 * @param valueA - First array to compare
 * @param valueB - Second array to compare
 * @param equalityFn - Optional function to determine if two elements are equal
 * @returns boolean indicating whether arrays are equal
 */
export function AreArraysEqual<T, U = T>(
	valueA: T[],
	valueB: U[],
	equalityFn?: (a: T, b: U, index: number) => boolean
): boolean {
	// Check array lengths first for quick fail
	if (valueA.Len() !== valueB.Len()) {
		return false
	}

	// Use the provided equality function or our default one
	const compareFn = equalityFn || AreEqual

	// Check each element in the arrays
	return valueA.All((item, index) => compareFn(item, valueB[index], index))
}

/**
 * Compare two values of any type with special handling for Options, IDs, arrays, and objects
 *
 * @param valueA First value to compare
 * @param valueB Second value to compare
 * @returns boolean indicating if values are equal
 */
export function AreEqual(valueA: any, valueB: any): boolean {
	// Unwrap both values if they're Options
	const unwrappedA = opt.IsOpt(valueA) ? valueA.UnwrapOrNil() : valueA
	const unwrappedB = opt.IsOpt(valueB) ? valueB.UnwrapOrNil() : valueB

	// Handle null/undefined
	if (isNil(unwrappedA) && isNil(unwrappedB)) {
		return true
	}

	if (isNil(unwrappedA) !== isNil(unwrappedB)) {
		return false
	}

	// Handle entity objects with IDs
	if (
		valid.IsObject(unwrappedA) &&
		valid.IsObject(unwrappedB) &&
		"id" in unwrappedA &&
		"id" in unwrappedB
	) {
		// Compare by ID string value to avoid custom Equal method issues
		return String(unwrappedA.id) === String(unwrappedB.id)
	}

	// Handle arrays - use internal comparison directly to avoid circular dependency
	if (Array.isArray(unwrappedA) && Array.isArray(unwrappedB)) {
		// Check array lengths first for quick fail
		if (unwrappedA.length !== unwrappedB.length) {
			return false
		}

		// Compare each element in the arrays
		return unwrappedA.All((item, index) => AreEqual(item, unwrappedB[index]))
	}

	// Handle regular objects
	if (
		valid.IsObject(unwrappedA) &&
		valid.IsObject(unwrappedB) &&
		!Array.isArray(unwrappedA) &&
		!Array.isArray(unwrappedB)
	) {
		// Try deep comparison via JSON stringify
		try {
			return JSON.stringify(unwrappedA) === JSON.stringify(unwrappedB)
		} catch (e) {
			// Fallback to reference equality if JSON stringify fails
			return unwrappedA === unwrappedB
		}
	}

	// Default to reference equality for primitives
	return unwrappedA === unwrappedB
}

/**
 * Converts a string to kebab-case format (lowercase with hyphens instead of spaces)
 * @param s - The string to convert
 * @returns The kebab-case formatted string
 * @example
 * ToKebabCase("Containment Information") // returns "containment-information"
 * ToKebabCase("Upload Containment File") // returns "upload-containment-file"
 */
export function ToKebabCase(s: string): string {
	return s.ToLower().Replace(/\s+/g, "-")
}
